import { isNull } from 'lodash';
import { useCallback } from 'react';

import {
  atlasWorkOrder,
  completedDate,
  created,
  createdBy,
  dueDate,
  serverAtlasType,
  status,
  tags,
  taskLegacyAtlasVendorColumn,
  taskVendorHeaderColumn,
} from 'components/data/tasks/tasks';
import * as workCampaignData from 'components/data/tasks/workCampaign';
import {
  collaborators,
  dueDate as workOrderDueDate,
  name as workOrderName,
  status as workOrderStatus,
  workCampaign,
  workOrderVendorHeaderColumn,
} from 'components/data/tasks/workOrders';
import { useRefetchWorkContainerHistory } from 'horizon/components/WorkContainerHistory/useRefetchWorkContainerHistory';
import {
  GetTaskListFiltersDocument,
  TaskDetailDocument,
  useEditTaskMutation,
  useEditWorkCampaignMutation,
  useEditWorkOrderMutation,
} from 'types/atlas-graphql';
import {
  CAMPAIGN,
  INSPECT_TASK,
  INSPECTION_TASK,
  INTERNAL_BLADE_INSPECTION_TASK,
  OTHER_TASK,
  REPAIR_TASK,
  WORK_ORDER,
} from 'utils/constants';
import { inlineEditable } from 'utils/editable/InlineEditable/InlineEditable';
import { useFeatures } from 'utils/features';

// This hook uses a combination of both rules and the isEditButtonVisible arg
// to determine whether to show the edit button.
// This is to continue to deprecate the use of rules going forward but need a way
// to dynamically show the button for ongoing work. - AN 9/27/23
export function useStandardFields(id = '', isEditButtonVisible = true) {
  const [editTask] = useEditTaskMutation();
  const [editWorkCampaign] = useEditWorkCampaignMutation();
  const [editWorkOrder] = useEditWorkOrderMutation();
  const refetchHistory = useRefetchWorkContainerHistory();

  const { WORK_ORDERS, COLLABORATORS } = useFeatures().features;

  const onWOSave = useCallback(
    async (input) => {
      if (isNull(input)) {
        return;
      }
      await editWorkOrder({
        variables: {
          id,
          input,
        },
        // Filter options need to be updated after work order data is saved
        refetchQueries: ['workOrder', 'WorkOrderDetail', { query: GetTaskListFiltersDocument }],
      });
      refetchHistory();
    },
    [id, editWorkOrder, refetchHistory]
  );

  const onTaskSave = useCallback(
    async (input) => {
      if (isNull(input)) {
        return;
      }
      await editTask({
        variables: {
          id,
          input,
        },
        refetchQueries: [{ query: TaskDetailDocument, variables: { id } }],
      });
      refetchHistory();
    },
    [id, editTask, refetchHistory]
  );

  const onCampaignSave = useCallback(
    async (input) => {
      if (isNull(input)) {
        return;
      }
      await editWorkCampaign({
        variables: {
          id,
          input,
        },
      });
    },
    [id, editWorkCampaign]
  );

  const paidTaskFeatures = WORK_ORDERS
    ? {
        tags: inlineEditable(tags, { onSave: onTaskSave }, isEditButtonVisible),
        parent: inlineEditable(atlasWorkOrder(), { onSave: onTaskSave }, isEditButtonVisible),
      }
    : {};
  const paidWOFeatures = COLLABORATORS
    ? { collaborators: inlineEditable(collaborators, { onSave: onWOSave }, isEditButtonVisible) }
    : {};

  const standardTaskFields = {
    task_type: serverAtlasType,
    status: inlineEditable(status, { onSave: onTaskSave }, isEditButtonVisible),
    created: created,
    created_by: createdBy,
    due_date: inlineEditable(dueDate, { onSave: onTaskSave }, isEditButtonVisible),
    completed_date: inlineEditable(completedDate, { onSave: onTaskSave }, isEditButtonVisible),
    vendor_organization: taskVendorHeaderColumn,
    vendor: taskLegacyAtlasVendorColumn,
    ...paidTaskFeatures,
  };

  return {
    [CAMPAIGN]: {
      name: inlineEditable(workCampaignData.name, { onSave: onCampaignSave }, isEditButtonVisible),
      status: inlineEditable(
        workCampaignData.status,
        { onSave: onCampaignSave },
        isEditButtonVisible
      ),
      created: workCampaignData.created,
      created_by: workCampaignData.createdBy,
      due_date: inlineEditable(
        workCampaignData.dueDate,
        { onSave: onCampaignSave },
        isEditButtonVisible
      ),
    },
    [WORK_ORDER]: {
      name: inlineEditable(workOrderName, { onSave: onWOSave }, isEditButtonVisible),
      status: inlineEditable(
        workOrderStatus,
        { onSave: onWOSave, id: 'work-order-status-edit' },
        isEditButtonVisible
      ),
      created: workCampaignData.created, // fine to reuse, not editable
      created_by: workCampaignData.createdBy, // fine to reuse, not editable
      parent: inlineEditable(workCampaign, { onSave: onWOSave }, isEditButtonVisible),
      due_date: inlineEditable(
        workOrderDueDate,
        { onSave: onWOSave, id: 'work-order-due-date-edit' },
        isEditButtonVisible
      ),
      vendor_organization: inlineEditable(
        workOrderVendorHeaderColumn,
        { onSave: onWOSave },
        isEditButtonVisible
      ),
      ...paidWOFeatures,
    },
    [REPAIR_TASK]: standardTaskFields,
    [INSPECT_TASK]: standardTaskFields,
    [OTHER_TASK]: standardTaskFields,
    [INTERNAL_BLADE_INSPECTION_TASK]: standardTaskFields,
    [INSPECTION_TASK]: standardTaskFields,
  };
}
